import Redirect from "@/services/redirect";
import { isConsumedExtrasellHistoryItem } from "@/services/extrasellPayload";

/**
 * Helpers for confirmation extrasell preview (?testLevel=vanguard_only).
 *
 * Upsell preview can pass parentChoiceId so only that parent's upsell is shown
 * (Order Pages with several initialChoices groups otherwise elect every group).
 *
 * Cross-sell preview (?step=N) must skip post-upsells: the extrasell lambda only
 * fetches cross-sells when upsells are empty, and looks them up at currentStep+1.
 * Marking the previous history stepType as "upsell" short-circuits fetchUpsells.
 */

export function electPreviewInitialChoices({
  productDetails,
  productId,
  parentChoiceId,
  electItemOfGroup,
}) {
  if (!productId) {
    return [];
  }

  if (parentChoiceId) {
    return [
      {
        productDetailsId: productId,
        qty: 1,
        choiceId: String(parentChoiceId),
      },
    ];
  }

  const groups = [...(productDetails?.initialChoices || [])];

  if (groups.length === 0) {
    const choices = productDetails?.choices || [];
    const visible = choices.filter((c) => c.show);
    if (visible.length === 0) {
      return [];
    }
    const w = Math.floor(100 / visible.length);
    groups.push(
      visible.map((c, i) => ({
        id: c.id,
        weight: i < visible.length - 1 ? w : 100 - w * (visible.length - 1),
      })),
    );
  }

  const elected = [];
  for (const group of groups) {
    const item = electItemOfGroup(group);
    if (item?.id != null) {
      elected.push({
        productDetailsId: productId,
        qty: 1,
        choiceId: item.id,
      });
    }
  }
  return elected;
}

/**
 * Payload that makes extrasell-intelligence return the cross-sell at Dynamo step
 * `targetStep` (SF OrderPageItem.Step__c), bypassing post-upsells.
 */
export function buildCrossSellPreviewRequest(
  domainId,
  electedInitialChoices,
  targetStep,
) {
  const step = Number.isFinite(targetStep) && targetStep > 0 ? targetStep : 1;
  const choices =
    Array.isArray(electedInitialChoices) && electedInitialChoices.length > 0
      ? electedInitialChoices
      : [];
  if (choices.length === 0) {
    throw new Error(
      "buildCrossSellPreviewRequest requires at least one elected initial choice",
    );
  }

  return {
    domainId,
    history: [{ step: 0, stepType: "upsell", choices }],
    currentStep: step - 1,
    previousStepType: "upsell",
  };
}

export function buildUpsellPreviewRequest(domainId, electedInitialChoices) {
  return {
    domainId,
    history: [{ step: 0, stepType: "initial", choices: electedInitialChoices }],
    currentStep: 0,
    previousStepType: "initial",
  };
}

/**
 * Build a cart shape suitable for orderHistory seeding in preview mode so
 * ConfirmationGifts / skipProcess share the same store path as a real order.
 */
export function buildPreviewHistoryCart(
  productDetails,
  productId,
  electedInitialChoices,
) {
  const choicesById = new Map(
    (productDetails?.choices || []).map((choice) => [
      String(choice.id),
      choice,
    ]),
  );

  return {
    domainId: window?.globalConfig?.domainId || null,
    items: (electedInitialChoices || []).map((elected) => {
      const choice = choicesById.get(String(elected.choiceId)) || {
        id: elected.choiceId,
      };
      return {
        id: productId,
        qty: elected.qty || 1,
        name: productDetails?.name || choice.name || "Preview",
        choice: {
          id: elected.choiceId,
          name: choice.name || "Preview",
        },
      };
    }),
    coupons: {},
  };
}

export function buildPreviewOrder({
  externalOrderId,
  paymentType = "sepa",
  billingEmail = null,
}) {
  return {
    externalOrderId,
    orderType: "initial",
    paymentType: paymentType || "sepa",
    status: "completed",
    url: window.location.href,
    billingEmail: billingEmail || null,
    testLevel: "vanguard_only",
  };
}

const VANGUARD_ONLY_PREVIEW_TAB_KEY = "vanguard_only_preview_tab";

export function markVanguardOnlyPreviewTab() {
  try {
    sessionStorage.setItem(VANGUARD_ONLY_PREVIEW_TAB_KEY, "1");
  } catch {
    /* storage unavailable */
  }
}

export function hasVanguardOnlyPreviewTab() {
  try {
    return sessionStorage.getItem(VANGUARD_ONLY_PREVIEW_TAB_KEY) === "1";
  } catch {
    return false;
  }
}

/**
 * Fake-pay confirmation must not re-seed an initial order once a preview
 * extrasell chain already exists. Re-seeding asks Magellan for postUpsells
 * again and the same upsell reappears after accept/skip.
 *
 * OPM "Prévisualiser" is a different case: a new tab with leftover
 * localStorage from a previous test. That tab has no session flag, so
 * it must seed a fresh Magellan preview instead of resuming CS2.
 */
export function shouldInitializePreview(
  handoff,
  scopedHistory,
  { isOpmTargetedPreview = false, isColdPreviewTab = false } = {},
) {
  if (isOpmTargetedPreview || isColdPreviewTab) {
    return true;
  }
  return (
    !handoff && (!Array.isArray(scopedHistory) || scopedHistory.length === 0)
  );
}

/**
 * Accept must use the same preview chain extrasell displays. Skip already
 * mutates unscoped orderHistory; the URL-scoped last item can be empty on
 * the order page even though the vanguard_only seed is still in storage.
 */
export function resolvePreviousOrderForExtrasell({
  currentPageLastOrder = null,
  orderHistory = [],
  paramPlusOrderId = null,
} = {}) {
  if (currentPageLastOrder) {
    return currentPageLastOrder;
  }

  const all = Array.isArray(orderHistory) ? orderHistory : [];
  const previewScoped = scopePreviewOrderHistory(all, {
    handoffItem: resolvePreviewHandoffHistoryItem(all, paramPlusOrderId),
  });
  return previewScoped[previewScoped.length - 1]?.order || null;
}

/**
 * Fake-pay handoff only: reuse history when PARAM_PLUS orderId matches a
 * vanguard_only seeded entry. Avoids treating leftover domain history as preview.
 */
export function resolvePreviewHandoffHistoryItem(history, paramPlusOrderId) {
  if (!paramPlusOrderId || !Array.isArray(history) || history.length === 0) {
    return null;
  }

  const match = history.find(
    (item) => item?.order?.externalOrderId === paramPlusOrderId,
  );
  if (!match) {
    return null;
  }
  if (match.order?.testLevel !== "vanguard_only") {
    return null;
  }
  return match;
}

const isInitialOrderType = (orderType) => !orderType || orderType === "initial";

const isPreviewExtrasellOrder = (order) =>
  order?.testLevel === "vanguard_only" &&
  (order.orderType === "upsell" || order.orderType === "crosssell");

/**
 * Waiting strips ?testLevel= from the URL. PARAM_PLUS still carries it so
 * confirmation extrasell must keep preview scoping after accept.
 */
export function isVanguardOnlyExtrasellSession() {
  if (typeof window === "undefined") {
    return false;
  }
  const params = new URLSearchParams(window.location.search || "");
  if (params.get("testLevel") === "vanguard_only") {
    return true;
  }
  return Redirect.getParamPlusData()?.testLevel === "vanguard_only";
}

function isPreviewChainItem(item) {
  if (item?.order?.testLevel !== "vanguard_only") {
    return false;
  }
  if (isPreviewExtrasellOrder(item.order)) {
    return true;
  }
  return isConsumedExtrasellHistoryItem(item);
}

function isFreshPreviewInitial(item) {
  if (!isInitialOrderType(item?.order?.orderType)) {
    return false;
  }
  if (isConsumedExtrasellHistoryItem(item)) {
    return false;
  }
  return item.action === "order" || item.action == null;
}

function dropTrailingLeftoverInitials(all, startIndex) {
  let endIndex = all.length - 1;
  while (
    endIndex > startIndex &&
    isInitialOrderType(all[endIndex]?.order?.orderType)
  ) {
    endIndex -= 1;
  }
  return all.slice(startIndex, endIndex + 1);
}

/**
 * Walk back through extrasells to the session initial, then drop any
 * trailing initial that a confirmation reset may have appended.
 * Last item must stay the latest extrasell so Magellan does not restart
 * the upsell after CS2.
 *
 * A fake-pay handoff that is itself a fresh initial is a NEW preview
 * session: do not walk back into a previous extrasell run, or the new
 * seed is dropped and the first offer never appears.
 */
function slicePreviewExtrasellChain(
  all,
  fromIndex,
  { fromHandoff = false } = {},
) {
  if (fromHandoff && isFreshPreviewInitial(all[fromIndex])) {
    return dropTrailingLeftoverInitials(all, fromIndex);
  }

  let startIndex = fromIndex;
  while (startIndex > 0 && isPreviewChainItem(all[startIndex - 1])) {
    startIndex -= 1;
  }
  while (
    startIndex > 0 &&
    !isInitialOrderType(all[startIndex]?.order?.orderType)
  ) {
    startIndex -= 1;
  }

  return dropTrailingLeftoverInitials(all, startIndex);
}

/**
 * Isolate the preview extrasell chain so buildPayload / skip do not send
 * unrelated prior orders from the same domain localStorage.
 * Keeps the vanguard_only seed and any following upsell/crosssell steps.
 */
export function scopePreviewOrderHistory(
  history,
  { handoffItem = null, previewHistoryItem = null } = {},
) {
  const all = Array.isArray(history) ? history : [];

  if (handoffItem?.order?.externalOrderId) {
    const orderId = handoffItem.order.externalOrderId;
    const index = all.findIndex(
      (item) => item?.order?.externalOrderId === orderId,
    );
    if (index >= 0) {
      return slicePreviewExtrasellChain(all, index, { fromHandoff: true });
    }
    return [handoffItem];
  }

  let seedIndex = -1;
  for (let i = all.length - 1; i >= 0; i -= 1) {
    const order = all[i]?.order;
    if (
      order?.testLevel === "vanguard_only" &&
      (!order.orderType || order.orderType === "initial")
    ) {
      seedIndex = i;
      break;
    }
  }

  if (seedIndex < 0) {
    for (let i = all.length - 1; i >= 0; i -= 1) {
      if (all[i]?.order?.testLevel === "vanguard_only") {
        seedIndex = i;
        break;
      }
    }
  }

  if (seedIndex >= 0) {
    return slicePreviewExtrasellChain(all, seedIndex);
  }

  if (previewHistoryItem) {
    return [previewHistoryItem];
  }

  return [];
}

/**
 * Waiting prefetch and extrasell payload must not reuse a leftover
 * vanguard_only chain when the current order is a real payment.
 */
export function selectExtrasellHistoryForPayload({
  orderHistory = [],
  currentPageOrderHistory = [],
  paramPlusOrderId = null,
  isPreviewSession = false,
} = {}) {
  if (!isPreviewSession) {
    return currentPageOrderHistory;
  }

  const previewScoped = scopePreviewOrderHistory(orderHistory, {
    handoffItem: resolvePreviewHandoffHistoryItem(
      orderHistory,
      paramPlusOrderId,
    ),
  });

  return previewScoped.length > 0 ? previewScoped : currentPageOrderHistory;
}
